/* =============================================================================
   A queue over the ONE player.

   The transport — what is playing, what comes next, what happens when a track
   ends — is the same question for a set of recordings and for a set of class
   videos, so it is answered once here rather than inside either surface.

   It deliberately owns no element and no <audio>: the element belongs to
   `MediaPlayer`, and this owns only the position in a list. That split is what
   lets the same queue drive a docked audio bar and a full-screen video.
   ============================================================================= */
import { useCallback, useMemo, useRef, useState } from "react";
import type { MediaPlayerHandle } from "./MediaPlayer";

/** The minimum a thing needs to be queueable. */
export interface Playable {
  id: string;
}

export interface MediaQueue<T extends Playable> {
  /** What is playing, or null when nothing is. */
  current: T | null;
  /** Start this item, or toggle it if it is already current. */
  play: (item: T) => void;
  /** Move by ±1 through the queue; a no-op at either end. */
  step: (delta: number) => void;
  /** Stop and clear. */
  stop: () => void;
  /** Handed to `MediaPlayer`'s `onReady` so the queue can drive transport. */
  attach: (handle: MediaPlayerHandle) => void;
  /** Wire to `MediaPlayer`'s `onEnded` to advance. */
  onEnded: () => void;
}

export function useMediaQueue<T extends Playable>(items: T[]): MediaQueue<T> {
  const [currentId, setCurrentId] = useState<string | null>(null);
  const handleRef = useRef<MediaPlayerHandle | null>(null);

  // The list changes identity on every parent render; a ref keeps the ended
  // handler from closing over a stale copy.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const currentIdRef = useRef<string | null>(null);
  currentIdRef.current = currentId;

  const play = useCallback((item: T) => {
    if (currentIdRef.current === item.id) {
      handleRef.current?.toggle();
      return;
    }
    // A new id remounts the player, which autoplays; there is nothing to toggle.
    handleRef.current = null;
    setCurrentId(item.id);
  }, []);

  const step = useCallback((delta: number) => {
    const list = itemsRef.current;
    const i = list.findIndex((m) => m.id === currentIdRef.current);
    if (i < 0) return;
    const next = list[i + delta];
    if (next) {
      handleRef.current = null;
      setCurrentId(next.id);
    }
  }, []);

  const stop = useCallback(() => {
    handleRef.current?.pause();
    handleRef.current = null;
    setCurrentId(null);
  }, []);

  const attach = useCallback((h: MediaPlayerHandle) => {
    handleRef.current = h;
  }, []);

  const onEnded = useCallback(() => step(1), [step]);

  const current = useMemo(
    () => items.find((m) => m.id === currentId) ?? null,
    [items, currentId],
  );

  return { current, play, step, stop, attach, onEnded };
}
